import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import CookieConsent from "../components/CookieConsent";

function LandingNavbar() {
  const { t } = useTranslation("landing");
  const { t: tCommon } = useTranslation("common");
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
      <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
        <Link to="/" className="text-xl font-bold text-white">
          {tCommon("brand")}
        </Link>

        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-3 sm:gap-4">
          <Link
            to="/login"
            className="text-sm font-medium text-white/90 hover:text-white transition-colors"
          >
            {t("nav.login")}
          </Link>
          <Link
            to="/signup"
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-50 transition-colors"
          >
            {t("nav.signUpFree")}
          </Link>
          <LanguageSwitcher />
        </nav>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          className="sm:hidden p-2 rounded-md text-white/90 hover:text-white hover:bg-white/10"
          aria-label="Toggle menu"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <nav className="sm:hidden bg-indigo-800/95 backdrop-blur-sm border-t border-white/10 px-4 py-3 flex flex-col gap-3">
          <Link
            to="/login"
            onClick={() => setMenuOpen(false)}
            className="text-sm font-medium text-white/90 hover:text-white transition-colors py-2"
          >
            {t("nav.login")}
          </Link>
          <Link
            to="/signup"
            onClick={() => setMenuOpen(false)}
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-50 transition-colors text-center"
          >
            {t("nav.signUpFree")}
          </Link>
          <div className="py-1">
            <LanguageSwitcher />
          </div>
        </nav>
      )}
    </header>
  );
}

function HeroSection() {
  const { t } = useTranslation("landing");

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800">
      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-purple-500/20 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-indigo-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-5xl px-4 py-32 text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight animate-fade-in">
          {t("hero.headline")}
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-indigo-100 max-w-2xl mx-auto leading-relaxed">
          {t("hero.subheadline")}
        </p>
        <div className="mt-10">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-lg font-bold text-indigo-600 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200"
          >
            {t("hero.cta")}
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>

        {/* Visual: Invoice → Excel flow */}
        <div className="mt-16 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
          <div className="flex flex-col items-center gap-2 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-4 sm:px-6 sm:py-5 text-white">
            <span className="text-3xl">📄</span>
            <span className="text-xs font-medium">{t("hero.flow.invoice")}</span>
          </div>
          <svg className="h-6 w-6 text-white/60 flex-shrink-0 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          <div className="flex flex-col items-center gap-2 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-4 sm:px-6 sm:py-5 text-white">
            <span className="text-3xl">🤖</span>
            <span className="text-xs font-medium">{t("hero.flow.aiOcr")}</span>
          </div>
          <svg className="h-6 w-6 text-white/60 flex-shrink-0 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          <div className="flex flex-col items-center gap-2 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-4 sm:px-6 sm:py-5 text-white">
            <span className="text-3xl">📊</span>
            <span className="text-xs font-medium">{t("hero.flow.excel")}</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
        <svg className="h-6 w-6 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const { t } = useTranslation("landing");

  const steps = [
    { icon: "📤", key: "step1" as const },
    { icon: "⚡", key: "step2" as const },
    { icon: "📥", key: "step3" as const },
  ];

  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-5xl px-4">
        <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900">
          {t("howItWorks.title")}
        </h2>
        <div className="mt-16 grid gap-8 sm:grid-cols-3">
          {steps.map((step, i) => (
            <div
              key={step.key}
              className="relative flex flex-col items-center text-center group"
            >
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-indigo-50 text-4xl group-hover:scale-110 transition-transform duration-200">
                {step.icon}
              </div>
              <div className="absolute -top-2 -left-2 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">
                {i + 1}
              </div>
              <h3 className="mt-6 text-xl font-semibold text-slate-900">
                {t(`howItWorks.${step.key}.title`)}
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                {t(`howItWorks.${step.key}.description`)}
              </p>
              {step.key === "step3" && (
                <p className="mt-2 text-xs text-indigo-600 font-medium">
                  {t("howItWorks.step3.subtitle")}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const { t } = useTranslation("landing");

  const features = [
    { icon: "🎯", key: "accuracy" as const },
    { icon: "💰", key: "payPerPage" as const },
    { icon: "📦", key: "batch" as const },
    { icon: "🇨🇾", key: "cyprus" as const },
    { icon: "🛡️", key: "gdpr" as const },
    { icon: "🗑️", key: "autoDelete" as const },
    { icon: "📤", key: "importReady" as const },
  ];

  return (
    <section className="py-20 sm:py-28 bg-slate-50">
      <div className="mx-auto max-w-5xl px-4">
        <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900">
          {t("features.title")}
        </h2>
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.key}
              className="rounded-2xl bg-white p-6 shadow-sm border border-slate-100 hover:shadow-md hover:-translate-y-1 transition-all duration-200"
            >
              <span className="text-3xl">{feature.icon}</span>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                {t(`features.${feature.key}.title`)}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                {t(`features.${feature.key}.description`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const { t } = useTranslation("landing");
  const [pages, setPages] = useState(50);

  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-3xl px-4 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
          {t("pricing.title")}
        </h2>
        <p className="mt-4 text-4xl sm:text-5xl font-extrabold text-indigo-600">
          {t("pricing.perPage")}
        </p>
        <p className="mt-4 text-slate-600">
          {t("pricing.description")}
        </p>

        {/* Mini calculator */}
        <div className="mt-12 rounded-2xl bg-slate-50 border border-slate-200 p-8">
          <label htmlFor="page-calc" className="block text-sm font-medium text-slate-700">
            {t("pricing.calculatorLabel")}
          </label>
          <input
            id="page-calc"
            type="range"
            min={1}
            max={500}
            value={pages}
            onChange={(e) => setPages(Number(e.target.value))}
            className="mt-4 w-full h-2 rounded-full appearance-none bg-indigo-200 accent-indigo-600 cursor-pointer"
          />
          <p className="mt-4 text-2xl font-bold text-slate-900">
            {t("pricing.calculatorResult", { cost: pages })}
          </p>
          <p className="mt-2 text-sm text-slate-500">
            {t("pricing.pagesPerMonth", { count: pages })}
          </p>
        </div>

        <p className="mt-8 text-lg font-medium text-emerald-600">
          🎁 {t("pricing.freeCredit")}
        </p>
        <div className="mt-8">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-8 py-4 text-lg font-bold text-white shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all duration-200"
          >
            {t("pricing.cta")}
          </Link>
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  const { t } = useTranslation("landing");

  const signals = [
    { emoji: "🇪🇺", key: "euHosted" as const },
    { emoji: "🔒", key: "encryption" as const },
    { emoji: "🗑️", key: "autoDelete" as const },
    { emoji: "✅", key: "gdpr" as const },
  ];

  return (
    <section className="py-12 bg-slate-100 border-y border-slate-200">
      <div className="mx-auto max-w-5xl px-4">
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-12">
          {signals.map((signal) => (
            <div key={signal.key} className="flex items-center gap-2 text-slate-700">
              <span className="text-xl">{signal.emoji}</span>
              <span className="text-sm font-medium">{t(`trust.${signal.key}`)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCtaSection() {
  const { t } = useTranslation("landing");

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
      <div className="mx-auto max-w-3xl px-4 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white">
          {t("finalCta.headline")}
        </h2>
        <div className="mt-10">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-lg font-bold text-indigo-600 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200"
          >
            {t("finalCta.cta")}
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

function LandingFooter() {
  const { t } = useTranslation("landing");
  const { t: tCommon } = useTranslation("common");

  return (
    <footer className="bg-slate-900 border-t border-slate-800">
      <div className="mx-auto max-w-5xl px-4 py-6 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
        <span>© {new Date().getFullYear()} {tCommon("brand")}</span>
        <nav className="flex flex-wrap items-center justify-center gap-4">
          <Link to="/privacy" className="hover:text-white transition-colors">
            {t("footer.privacy")}
          </Link>
          <Link to="/terms" className="hover:text-white transition-colors">
            {t("footer.terms")}
          </Link>
          <Link to="/cookies" className="hover:text-white transition-colors">
            {t("footer.cookies")}
          </Link>
          <Link to="/support" className="hover:text-white transition-colors">
            {t("footer.support")}
          </Link>
        </nav>
      </div>
    </footer>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();
  const { t } = useTranslation("landing");

  useEffect(() => {
    document.title = t("seo.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("seo.description"));
    }
  }, [t]);

  if (loading) {
    return null;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen">
      <LandingNavbar />
      <HeroSection />
      <HowItWorksSection />
      <FeaturesSection />
      <PricingSection />
      <TrustSection />
      <FinalCtaSection />
      <LandingFooter />
      <CookieConsent />
    </div>
  );
}
